import * as Sentry from '@sentry/react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import { ETAPaymentMethod, isPhonePaymentMethod } from '~/api/ETA';
import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import { PaymentSystem } from '~/api/Order';
import { company } from '~/company/Company';
import AwsPayment, {
  type AwsPaymentRef,
} from '~/components/AwsPayment/AwsPayment';
import Collapsible from '~/components/Collapsible/Collapsible';
import Icon from '~/components/Icon/Icon';
import { ModalType } from '~/components/Modal/interface';
import Price from '~/components/Price';
import Promocode from '~/components/Promocode/Promocode';
import Spinner from '~/components/Spinner/Spinner';
import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import { useHeaderSize } from '~/hooks/useHeaderSize';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { useSticky } from '~/hooks/useSticky';
import CheckoutContentMobile from '~/pages/Checkout/CheckoutContentMobile';
import { PaymentMethods } from '~/pages/Checkout/components/PaymentMethods/PaymentMethods';
import { useIsTablet } from '~/pages/Order/hooks';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { notificationService } from '~/services/NotificationService';
import { verifoneService } from '~/services/Verifone';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import { awsPaymentStore } from '~/stores/AwsPaymentStore';
import type { CartItem, CartItemOffer } from '~/stores/CartStore';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import { getAddressModalParams } from '~/utils/addressModal';
import { formatPriceWithCurrency } from '~/utils/formaters';
import { clearStoredPromocode, getStoredPromocode } from '~/utils/referralLink';

import styles from './CheckoutContent.module.scss';
import { ActiveDeliveryMethodProductList } from './components/ActiveDeliveryMethodProductList';
import { DeliveryMethodsTab } from './components/DeliveryMethodsTab';
import InfoItem from './components/InfoItem/InfoItem';
import PointsPanel from './components/PointsPanel/PointsPanel';

import '@adyen/adyen-web/styles/adyen.css';

const CheckoutContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const stripe = useStripe();
  const elements = useElements();
  const isTablet = useIsTablet();
  const innerAsideRef = useRef<HTMLDivElement>(null);
  const outerAsideRef = useRef<HTMLDivElement>(null);
  const { headerHeight } = useHeaderSize();
  const awsPaymentRef = useRef<AwsPaymentRef>(null);
  const isLoadingConfirm =
    checkoutStore.isLoading ||
    orderStore.isLoading ||
    awsPaymentStore.isIframeLoading;

  const confirmOrderButtonTitle =
    company.useAwsPaymentMethod &&
    !checkoutStore?.payments?.length &&
    checkoutStore.activePaymentMethod === ETAPaymentMethod.Card
      ? t('addNewCard')
      : t('confirmOrder');

  useSticky({
    topSpacing: headerHeight,
    outerRef: outerAsideRef,
    innerRef: innerAsideRef,
  });

  const loyaltyPointsWorkWithPromocode =
    userStore.loyaltyPointsStore.workWithPromocode;
  const loyaltyPointsUse = userStore.loyaltyPointsStore.useLoyaltyPoints;
  const promocodeUse = cartStore.promotionStore.promocode.success;

  const handleSubmit = () => {
    let isValid = true;
    if (checkoutStore.nameVal.trim().length === 0) {
      isValid = false;
      mainStore.pushAlert('error', t('pleaseEnterName'));
    }

    if (!checkoutStore.emailVal.trim().length) {
      isValid = false;
      mainStore.pushAlert('error', t('pleaseEnterEmail'));
    }

    if (!checkoutStore.isEmailValid) {
      isValid = false;
      mainStore.pushAlert('error', t('emailInvalid'));
    }

    if (isValid) {
      const pay = () => {
        checkoutStore
          .orderCheckout(stripe, elements)
          .then(() => {
            const storedPromocode = getStoredPromocode();
            if (
              cartStore.promocode.success &&
              cartStore.promocode.value === storedPromocode?.code
            ) {
              clearStoredPromocode();
            }
          })
          .catch(() => {
            // TODO: handle
          });
      };
      Sentry.withScope((scope) => {
        scope.setTag('payment_flow', checkoutStore.activePaymentSystem);
        scope.setContext('payment_details', {
          payment_method: checkoutStore.activePaymentMethod,
          amount: cartStore.finalPrice,
          currency: orderStore.currency,
          is_mobile: mainStore.isRN,
        });
        Sentry.addBreadcrumb({
          category: 'payment',
          message: 'Payment flow initiated',
          level: 'info',
        });
      });

      mainStore.sendToRN('hapticFeedback', {
        count: 1,
        type: 'impactMedium',
      });

      if (company.useAwsPaymentMethod) {
        if (awsPaymentRef.current) {
          awsPaymentRef.current.handleSubmitForm();
          return;
        } else {
          pay();
        }
        return;
      }

      if (checkoutStore.activePaymentSystem === PaymentSystem.verifone) {
        Sentry.addBreadcrumb({
          category: 'payment',
          message: 'Verifone payment Starting checkout',
          level: 'info',
        });

        Sentry.addBreadcrumb({
          category: 'payment',
          message: 'Verifone payment Submitting payment',
          level: 'info',
        });

        if (
          (checkoutStore.isAddNewCard || !checkoutStore.paymentsMethodId) &&
          checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
          checkoutStore.activePaymentMethod !== MobilePaymentMethod.ApplePay &&
          checkoutStore.activePaymentMethod !== MobilePaymentMethod.GooglePay
        ) {
          verifoneService.initializePayment();
          return;
        }

        pay();
        return;
      }

      if (
        checkoutStore.activePaymentSystem === PaymentSystem.stripe ||
        checkoutStore.activePaymentSystem === PaymentSystem.adyen ||
        checkoutStore.activePaymentSystem === PaymentSystem.myfatoorah ||
        checkoutStore.activePaymentSystem === PaymentSystem.octo
      ) {
        pay();
      } else if (checkoutStore.activePaymentSystem === PaymentSystem.pvit) {
        if (checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR) {
          if (!checkoutStore.mobileMoneyPhoneVal.trim().length) {
            notificationService.error(t('mobileMoneyPhoneInvalid'));
            return;
          }
        }

        pay();
      } else if (checkoutStore.activePaymentMethod === ETAPaymentMethod.Cash) {
        pay();
      }
    }
  };

  const PHONE_PAYMENT_MIN_AMOUNT = 150;
  const PHONE_PAYMENT_MAX_AMOUNT = 500000;

  const minimalOrderValueError =
    cartStore.totalCartPrice.minimal_order_value_error?.length &&
    cartStore.totalCartPrice.minimal_order_value_error[0] ? (
      <>
        {t('errors:minCartError', {
          amount: formatPriceWithCurrency(
            cartStore.totalCartPrice.minimal_order_value_error[0]
              ?.minimalOrderValue / 100,
          ),
        })}
        <br />
        {t('errors:minCartErrorDetails', {
          amount: formatPriceWithCurrency(
            (cartStore.totalCartPrice.minimal_order_value_error[0]
              ?.minimalOrderValue -
              cartStore.totalCartPrice.minimal_order_value_error[0]
                ?.orderPriceWithoutDelivery) /
              100,
          ),
        })}
      </>
    ) : null;

  const isPhonePaymentMethodActive =
    checkoutStore.activePaymentMethod === ETAPaymentMethod.PhoneAirtel ||
    checkoutStore.activePaymentMethod === ETAPaymentMethod.PhoneMoov;
  const orderTotalForPhone = Number(cartStore.finalPrice) || 0;
  const isPhoneAmountOutOfRange =
    isPhonePaymentMethodActive &&
    (orderTotalForPhone < PHONE_PAYMENT_MIN_AMOUNT ||
      orderTotalForPhone > PHONE_PAYMENT_MAX_AMOUNT);
  const phonePaymentAmountError = isPhoneAmountOutOfRange ? (
    <>
      {orderTotalForPhone < PHONE_PAYMENT_MIN_AMOUNT
        ? t('errors:phonePaymentAmountTooLow', {
            min: formatPriceWithCurrency(PHONE_PAYMENT_MIN_AMOUNT),
          })
        : t('errors:phonePaymentAmountTooHigh', {
            max: formatPriceWithCurrency(PHONE_PAYMENT_MAX_AMOUNT),
          })}
    </>
  ) : null;

  const checkoutError = minimalOrderValueError || phonePaymentAmountError;

  const isDisabledForm = (): boolean => {
    if (
      cartStore.calculationProcess.isLoading ||
      cartStore.calculationProcess.isError
    ) {
      return true;
    }
    if (checkoutStore.isLoading) {
      return true;
    }

    if (awsPaymentStore.isIframeLoading) {
      return true;
    }

    if (orderStore.isETALoading) {
      return true;
    }

    if (
      checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
      checkoutStore.activePaymentSystem === PaymentSystem.stripe &&
      (stripe === null || elements === null)
    ) {
      return true;
    }

    if (
      checkoutStore.selectedDeliveryDate &&
      checkoutStore.selectedDeliveryDate !== '0' &&
      checkoutStore.selectedDeliverySlot
    ) {
      const selectedDate =
        checkoutStore.deliverySlots[checkoutStore.selectedDeliveryDate];

      if (!selectedDate) {
        throw new Error('Something went wrong');
      }

      const slot = selectedDate.find(
        ({ schedule_slot_id }) =>
          schedule_slot_id ===
          checkoutStore.selectedDeliverySlot?.schedule_slot_id,
      );
      if (!slot) {
        return true;
      }
    }

    if (
      checkoutStore.activePaymentMethod === ETAPaymentMethod.Card &&
      checkoutStore.activePaymentSystem !== PaymentSystem.myfatoorah &&
      checkoutStore.activePaymentSystem !== PaymentSystem.midtrans &&
      checkoutStore.activePaymentSystem !== PaymentSystem.amazon &&
      checkoutStore.activePaymentSystem !== PaymentSystem.adyen &&
      checkoutStore.activePaymentSystem !== PaymentSystem.verifone
    ) {
      return checkoutStore.isDisabled;
    }

    if (isPhoneAmountOutOfRange) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (checkoutStore.activePaymentSystem !== PaymentSystem.stripe) return;

    if (!stripe || !elements) return;

    checkoutStore.requestPayments().finally(() => {
      if (
        checkoutStore.isRefreshed &&
        !checkoutStore.isLoading &&
        !isDisabledForm() &&
        !minimalOrderValueError
      ) {
        checkoutStore.orderCheckout(stripe, elements);
      }
    });
  }, [checkoutStore.activePaymentSystem, stripe, elements]);

  useEffect(() => {
    if (checkoutStore.orderId) {
      return;
    }

    if (
      cartStore.promocode.success &&
      mainStore.isZero(cartStore.totalCartPrice.promocodeDiscount) &&
      cartStore.promocode.coupon?.minimumPurchase
    ) {
      mainStore.setIsInvalidPromocodePopover(true);
    }
  }, [cartStore.totalCartPrice]);

  useEffect(() => {
    if (checkoutStore.orderId || cartStore.cart.length) {
      return;
    }
    navigate('/cart', { replace: true });
  }, [cartStore.cart.length]);

  useEffect(() => {
    const deliveryZoneId =
      orderStore.etaCalculation?.warehouse.zone[checkoutStore.deliveryMethod]
        ?.id;
    orderStore.requestPaymentSystems(deliveryZoneId);
  }, []);

  useEffect(() => {
    checkoutStore.setAddressVal(userStore.deliveryAddress?.address1 || '');
    checkoutStore.setAddress2Val(userStore.deliveryAddress?.address2 || '');
    checkoutStore.setAddressFloorVal(
      userStore.deliveryAddress?.addressFloor || '',
    );
    checkoutStore.setAddressBldVal(userStore.deliveryAddress?.addressBld || '');
    checkoutStore.setAddressAptVal(userStore.deliveryAddress?.addressApt || '');
  }, [userStore.deliveryAddress, userStore.deliveryAddress?.address2]);

  useEffect(() => {
    if (!checkoutStore.orderId) {
      return;
    }

    if (
      ((isPhonePaymentMethod(
        checkoutStore.activePaymentMethod as ETAPaymentMethod,
      ) ||
        checkoutStore.activePaymentMethod === ETAPaymentMethod.QR) &&
        checkoutStore.activePaymentSystem === PaymentSystem.pvit) ||
      checkoutStore.activePaymentSystem === PaymentSystem.octo
    ) {
      return navigate(
        `/order-payment/${checkoutStore.orderId}?payment_method=${checkoutStore.activePaymentMethod}`,
        {
          replace: true,
        },
      );
    }

    navigate('/cabinet/orders', {
      replace: true,
      state: { linkBack: '/' },
    });
  }, [checkoutStore.orderId]);

  const handleCommentChange = (val: string) => {
    checkoutStore.setCommentVal(val);
  };

  useEffect(() => {
    if (userStore.deliveryAddress?.comment) {
      checkoutStore.setCommentVal(userStore.deliveryAddress?.comment || '');
    }
  }, [userStore.deliveryAddress?.addressId]);

  const handleChangeEmailModal = () => openModal(ModalType.ChangeEmail);

  const handleSelectDateModal = (
    index: number,
    order: CartCalculationOrderResponse,
  ) => {
    const delivery = checkoutStore.deliveries?.[index];

    if (!delivery) {
      return;
    }

    analyticsService.track(AnalyticsEvent.DELIVERY_METHOD_SELECT_TIME, {});

    checkoutStore.slotSelectionModalStore.setSelectedSlot(delivery, order);
    openModal(ModalType.SelectDate);
  };

  const hasExpressDelivery = checkoutStore.deliveries?.some(
    (delivery) => !delivery.slotDeliveryDetails,
  );

  const totalDeliveryFee = cartStore.deliveriesOrders.reduce(
    (acc, { is_delivery_free, delivery_info: { paid_price } }) => {
      if (is_delivery_free) {
        return acc;
      }
      return acc + paid_price;
    },
    0,
  );

  const handleDeliveryAddress = () => {
    const [targetModal, source, params] = getAddressModalParams('checkout', {
      [MODAL_EDIT_ADDRESS_PARAM]: userStore?.deliveryAddress?.addressId || '',
    });

    openModal(targetModal, source, params);
  };

  const handleOpenRecieverData = () => {
    openModal(ModalType.CheckoutReciever);
  };

  const personalInfo = checkoutStore.phoneVal
    ? `, ${mainStore.prettyPhoneNumber(checkoutStore.phoneVal ?? '')}`
    : '';

  const isDeliveryMethodSelectionAvailable =
    company.deliveryMethodSelectionAvailable &&
    checkoutStore.availableDeliveryMethods.length > 1;

  const isPromoLoading = cartStore.promotionStore.applyPromocodeMeta.isLoading;
  const openDeliveryModal = () => {
    openModal(ModalType.DeliveryInfo, 'checkout');
  };

  const propsStirng = (i: CartItem) => {
    return (i as CartItemOffer).selectableProperties &&
      Object.keys((i as CartItemOffer).selectableProperties).length > 0
      ? `, ${Object.values((i as CartItemOffer).selectableProperties)
          .map((i) => i.value)
          .join(', ')}`
      : '';
  };

  return (
    <>
      {isTablet ? (
        <CheckoutContentMobile
          isRestrictedPayment={isDisabledForm() || !!checkoutError}
          handleSelectDateModal={handleSelectDateModal}
          handleCommentChange={handleCommentChange}
          totalDeliveryFee={totalDeliveryFee}
          handleDeliveryAddress={handleDeliveryAddress}
          handleOpenRecieverData={handleOpenRecieverData}
          handleChangeEmailModal={handleChangeEmailModal}
        />
      ) : null}

      <div className="checkout desktop" data-company={company.name}>
        <div className="content-layout">
          <Link data-button="back" to="/cart" className={styles.backButton}>
            <Icon type="chevron" size={24} className={styles.backButtonIcon} />
            {t('backToCart')}
          </Link>
          <div className="checkout-layout">
            <div className="checkout-layout__content">
              <div className={styles.panel}>
                <div className={styles.panelSection}>
                  <div className={styles.panelTitleContainer}>
                    <p
                      className={classes([
                        styles.panelTitle,
                        styles.deliverySelectionTitle,
                      ])}
                    >
                      {t('checkout')}
                    </p>
                    {isDeliveryMethodSelectionAvailable && (
                      <span
                        className={classes([
                          styles.panelCaption,
                          styles.deliverySelectionCaption,
                        ])}
                      >
                        {t('deliveryMethods:caption')}
                      </span>
                    )}
                  </div>

                  {isDeliveryMethodSelectionAvailable && (
                    <DeliveryMethodsTab
                      activeTab={checkoutStore.deliveryMethod}
                      onTabClick={(method) => {
                        checkoutStore.setDeliveryMethod(method);
                        analyticsService.track(
                          AnalyticsEvent.DELIVERY_METHOD_SELECTED,
                          {
                            type:
                              method === ETADeliveryMethodType.ClickAndCollect
                                ? 'Pickup'
                                : 'Delivery',
                          },
                        );
                      }}
                      className={styles.deliveryMethodSelection}
                    />
                  )}

                  {checkoutStore.deliveryMethod ===
                  ETADeliveryMethodType.ClickAndCollect ? (
                    orderStore.etaCalculation && (
                      <InfoItem
                        attributeName="pick-up"
                        name={t('deliveryMethods:pickupFrom')}
                        value={orderStore.etaCalculation.warehouse.address}
                        onClick={handleDeliveryAddress}
                      />
                    )
                  ) : (
                    <InfoItem
                      attributeName="deliver-to"
                      name={t('deliverTo')}
                      value={userStore.deliveryAddress?.address1}
                      onClick={handleDeliveryAddress}
                    />
                  )}
                  {!checkoutStore.emailVal &&
                  checkoutStore.nameVal &&
                  personalInfo ? (
                    <InfoItem
                      attributeName="email"
                      name={t(checkoutStore.emailVal ? 'email' : 'enterEmail')}
                      value={checkoutStore.emailVal}
                      required={true}
                      onClick={handleOpenRecieverData}
                    />
                  ) : (
                    <InfoItem
                      attributeName="receiver"
                      name={t(
                        personalInfo ? 'receiver' : 'fillOutPersonalDetails',
                      )}
                      value={checkoutStore.nameVal}
                      additionalValue={personalInfo}
                      required={true}
                      onClick={handleOpenRecieverData}
                    />
                  )}
                </div>
              </div>

              <ActiveDeliveryMethodProductList
                isDeliveryMethodSelectionAvailable={
                  isDeliveryMethodSelectionAvailable
                }
                hasExpressDelivery={hasExpressDelivery}
                handleCommentChange={handleCommentChange}
                handleSelectDateModal={handleSelectDateModal}
              />
            </div>
            <div ref={outerAsideRef}>
              <div className="checkout-layout__summary" ref={innerAsideRef}>
                <div className={classes(['panel'])}>
                  {checkoutStore.isLoading ||
                    orderStore.isLoading ||
                    (isPromoLoading && <Spinner layout={true} />)}
                  <div className={styles.checkoutSummaryItemContainer}>
                    <div className="checkout-summary__item">
                      <Collapsible
                        dataAttributeName="items"
                        titleElem={
                          <div className="checkout-summary__header">
                            <div className="checkout-summary__header-text">
                              <p className="checkout-summary__header-text-title">
                                {t('orderValue')}
                                <Icon type="chevron" size={16} />
                              </p>
                            </div>
                            <Price
                              price={
                                cartStore.totalCartPrice.paidWithoutDiscount
                              }
                              classNameCurrentPrice="checkout-summary__header-price"
                            />
                          </div>
                        }
                        isCollapsed={true}
                      >
                        <ul className="checkout-summary__list">
                          {cartStore.selectedCartItems.map((i) => (
                            <li
                              key={i.id}
                              className="checkout-summary__list-item"
                            >
                              <div className="checkout-summary__list-item-info">
                                {i.name}
                                {propsStirng(i)}{' '}
                                {i.count > 1 && (
                                  <span className="checkout-summary__list-item-info-product-count">
                                    {i.count}x
                                  </span>
                                )}
                              </div>
                              <div className="checkout-summary__list-item-price">
                                {formatPriceWithCurrency(
                                  (i.discountPrice
                                    ? i.count * i.discountPrice
                                    : i.count * i.price) / 100,
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </Collapsible>
                    </div>
                    {checkoutStore.deliveryMethod !==
                      ETADeliveryMethodType.ClickAndCollect && (
                      <div className="checkout-summary__item">
                        <Collapsible
                          dataAttributeName="delivery-fee"
                          titleElem={
                            <div className="checkout-summary__header">
                              <div className="checkout-summary__header-text">
                                <p className="checkout-summary__header-text-title">
                                  {t('deliveryFeeLong')}{' '}
                                  {company.isDeliveryInfoModalEnabled && (
                                    <div
                                      data-tooltip="info"
                                      onClick={openDeliveryModal}
                                      className={styles.deliveryTooltip}
                                    >
                                      <Icon
                                        className="static"
                                        type="info"
                                        size={16}
                                      />
                                    </div>
                                  )}
                                  <Icon type="chevron" size={16} />
                                </p>
                              </div>
                              <Price
                                price={mainStore.convertPenceToPounds(
                                  totalDeliveryFee,
                                )}
                                classNameCurrentPrice="checkout-summary__header-price"
                              />
                            </div>
                          }
                          isCollapsed={true}
                        >
                          <ul className="checkout-summary__list">
                            {checkoutStore.deliveries?.map(
                              (delivery, index) => {
                                const key = delivery.slotDeliveryDetails
                                  ? delivery.slotDeliveryDetails
                                      ?.scheduleSlotId +
                                    delivery.slotDeliveryDetails?.currentDate
                                  : 'express';

                                const order = cartStore.deliveriesOrders[index];
                                const isDeliveryFree = order?.is_delivery_free;
                                const deliveryPrice = formatPriceWithCurrency(
                                  mainStore.convertPenceToPounds(
                                    order?.delivery_info.paid_price ?? 0,
                                  ),
                                );
                                const slotNumber = hasExpressDelivery
                                  ? index
                                  : index + 1;

                                if (!order) {
                                  return <Fragment key={key} />;
                                }

                                const items = delivery.itemsIds.reduce(
                                  (acc, i) =>
                                    acc +
                                    (cartStore.cart.find((c) => c.sku === i)
                                      ?.count ?? 0),
                                  0,
                                );

                                return (
                                  <li
                                    key={key}
                                    className="checkout-summary__list-item"
                                  >
                                    <div className="checkout-summary__list-item-info">
                                      {!slotNumber
                                        ? t('expressDelivery')
                                        : t('slot', { num: slotNumber })}{' '}
                                      <span className="checkout-summary__list-item-info-item-count">
                                        {items}{' '}
                                        {t(items > 1 ? 'items' : 'item')}
                                      </span>
                                    </div>
                                    <div className="checkout-summary__list-item-price">
                                      {isDeliveryFree ? (
                                        <p className="free">
                                          {t('deliveryFreeShort')}
                                        </p>
                                      ) : (
                                        <p className="slot-list__slot-fee">
                                          {deliveryPrice}
                                        </p>
                                      )}
                                    </div>
                                  </li>
                                );
                              },
                            )}
                          </ul>
                        </Collapsible>
                      </div>
                    )}
                    <div className="checkout-summary__item">
                      <ul className="checkout-taxes__list">
                        {!cartStore.totalCartPrice.taxPercent &&
                        !cartStore.totalCartPrice.taxAmount ? null : (
                          <li className="checkout-taxes__list-item">
                            <div className="checkout-taxes__list-item-title">
                              <p className="">
                                {cartStore.totalCartPrice.taxPercent > 0
                                  ? t('phrases:vatCheckout', {
                                      percent:
                                        cartStore.totalCartPrice.taxPercent,
                                    })
                                  : t('phrases:vat_')}
                              </p>
                            </div>
                            <Price
                              price={mainStore.convertPenceToPounds(
                                cartStore.totalCartPrice.taxAmount,
                              )}
                              classNameCurrentPrice="checkout-taxes__list-item-price"
                            />
                          </li>
                        )}
                        {cartStore.promocode.success === true && (
                          <li className="checkout-taxes__list-item">
                            <div className="checkout-taxes__promocode-code">
                              <p className="checkout-taxes__promocode-code-title">
                                {t('promocode')}
                              </p>
                              <p className="checkout-taxes__promocode-code-value">
                                {cartStore.promocode.value}
                              </p>
                            </div>
                            <Price
                              price={`-${cartStore.totalCartPrice.promocodeDiscount}`}
                              classNameCurrentPrice="checkout-taxes__promocode-discount"
                            />
                          </li>
                        )}
                        {userStore.loyaltyPointsStore.useLoyaltyPoints &&
                          cartStore.totalCartPrice.paidLoyaltyPointsPence >
                            0 && (
                            <li className="checkout-taxes__list-item">
                              <div className="checkout-taxes__promocode-code">
                                <p className="checkout-taxes__promocode-code-title">
                                  {t('loyalty:checkout:points')}
                                </p>
                                <p className="checkout-taxes__promocode-code-points">
                                  {`-${cartStore.totalCartPrice.paidLoyaltyPointsPence}`}
                                </p>
                              </div>
                              <Price
                                price={`-${cartStore.totalCartPrice.loyaltyPointsDiscount}`}
                                classNameCurrentPrice="checkout-taxes__promocode-discount"
                              />
                            </li>
                          )}
                      </ul>
                    </div>
                  </div>
                  <PointsPanel
                    classNames={styles.pointPanel}
                    disabled={!loyaltyPointsWorkWithPromocode && promocodeUse}
                  />
                  <Promocode
                    disabled={
                      !loyaltyPointsWorkWithPromocode && loyaltyPointsUse
                    }
                  />
                  <div className="checkout-summary__description-total">
                    <p className="checkout-summary__description-total-text">
                      {t('total')}
                    </p>
                    <Price
                      price={cartStore.totalCartPrice.paid}
                      classNameCurrentPrice="checkout-summary__description-total-price"
                    />
                  </div>
                </div>

                {!isTablet && (
                  <>
                    <div className="panel">
                      <PaymentMethods hideDisclaimer={true} />
                      {checkoutStore.activePaymentMethod ===
                        ETAPaymentMethod.Card &&
                        company.useAwsPaymentMethod && (
                          <AwsPayment ref={awsPaymentRef} />
                        )}
                      {!company.buttonOutPanelCheckout &&
                        !awsPaymentStore.isActiveAwsIframe && (
                          <button
                            data-button="confirm-order"
                            className="button _primary checkout-summary__button"
                            disabled={isDisabledForm() || !!checkoutError}
                            onClick={handleSubmit}
                          >
                            {isLoadingConfirm && (
                              <span className="spinner _white" />
                            )}
                            {!checkoutError
                              ? confirmOrderButtonTitle
                              : checkoutError}
                          </button>
                        )}
                      {checkoutStore.activePaymentMethod !==
                        ETAPaymentMethod.Cash &&
                        !isPhonePaymentMethod(
                          checkoutStore.activePaymentMethod as ETAPaymentMethod,
                        ) &&
                        checkoutStore.activePaymentMethod !==
                          ETAPaymentMethod.QR &&
                        !company.hideFreezeMoneyMessage && (
                          <p className="checkout-summary__description-text-conditions">
                            {t('phrases:moneyFreeze')}
                          </p>
                        )}
                      {!company.buttonOutPanelCheckout && (
                        <p className="checkout-summary__description-text-terms">
                          {t('phrases:byOrdering')}{' '}
                          <Link
                            data-link="terms"
                            to={'/legals'}
                            state={{ source: 'checkout' }}
                          >
                            {t('termsAndConditions')}
                          </Link>
                        </p>
                      )}
                    </div>
                    {company.buttonOutPanelCheckout &&
                      !awsPaymentStore.isActiveAwsIframe && (
                        <div>
                          <button
                            data-button="confirm-order"
                            className="button _primary checkout-summary__button"
                            disabled={isDisabledForm() || !!checkoutError}
                            onClick={handleSubmit}
                          >
                            {isLoadingConfirm && (
                              <span className="spinner _white" />
                            )}
                            {!checkoutError
                              ? confirmOrderButtonTitle
                              : checkoutError}
                          </button>
                          <p className="checkout-summary__description-text-terms">
                            {t('phrases:byOrdering')}{' '}
                            <Link
                              data-link="terms"
                              to={'/legals'}
                              state={{ source: 'checkout' }}
                            >
                              {t('termsAndConditions')}
                            </Link>
                          </p>
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {company.useAwsPaymentMethod &&
      checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash
        ? null
        : cartStore.cart.length > 0 && (
            <div
              className="mobile-bottom-button-container"
              id={UI_BOTTOM_COMPONENTS.CTA}
            >
              <button
                data-button="confirm-order"
                className={`button _primary _med ${
                  checkoutError ? '_center ' : ''
                }`}
                disabled={isDisabledForm() || !!checkoutError}
                onClick={handleSubmit}
              >
                {!checkoutError ? confirmOrderButtonTitle : checkoutError}
                {!checkoutError && (
                  <>
                    {isLoadingConfirm ? (
                      <span className="spinner _white" />
                    ) : (
                      <Price price={cartStore.finalPrice} />
                    )}
                  </>
                )}
              </button>
            </div>
          )}
    </>
  );
};

export default observer(CheckoutContent);
